/**
 * Centralised configuration for third-party travel-service affiliate URLs.
 *
 * iter103 — Kiwitaxi (airport transfers) + Localrent (car rentals).
 *
 * HOW TO REPLACE A URL
 * --------------------
 * Edit the matching env var in `/app/frontend/.env`:
 *   REACT_APP_KIWITAXI_AFFILIATE_URL  — Kiwitaxi affiliate shortlink
 *   REACT_APP_LOCALRENT_AFFILIATE_URL — Localrent affiliate shortlink
 * then rebuild / restart the frontend. No component changes required.
 *
 * SAFEGUARDS
 * ----------
 * • When a URL is missing or malformed, `isAffiliateServiceEnabled(key)`
 *   returns `false`, components hide the CTA, and a console warning is
 *   logged for admins. We never silently redirect to an unrelated page.
 * • We only honour `https://` URLs — any other scheme (javascript:,
 *   data:, mailto:) is rejected to prevent XSS via misconfigured env.
 * • We do NOT append arbitrary query parameters to the configured URL
 *   because the provider shortlinks are not documented to accept them.
 *   If deep-link support is confirmed later, extend `buildAffiliateUrl`.
 *
 * NAMING CONVENTION
 * -----------------
 * `service` keys are lowercase, normalised, and must match the labels
 * used by the analytics event schema (`kiwitaxi`, `localrent`).
 */

const SERVICES = {
  kiwitaxi: {
    key: 'kiwitaxi',
    label: 'Airport Transfers',
    envVar: 'REACT_APP_KIWITAXI_AFFILIATE_URL',
    url: (process.env.REACT_APP_KIWITAXI_AFFILIATE_URL || '').trim(),
    partnerName: 'Kiwitaxi',
  },
  localrent: {
    key: 'localrent',
    label: 'Car Rentals',
    envVar: 'REACT_APP_LOCALRENT_AFFILIATE_URL',
    url: (process.env.REACT_APP_LOCALRENT_AFFILIATE_URL || '').trim(),
    partnerName: 'Localrent',
  },
};

/** Validates a configured affiliate URL. Accepts only https:// shortlinks. */
function _isValid(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
}

// Fail loudly at module load so missing/malformed URLs are surfaced to
// admins in production console output — but never throw: the SPA must
// keep booting even when a single service is misconfigured.
Object.values(SERVICES).forEach((svc) => {
  if (!_isValid(svc.url)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[affiliateServices] ${svc.envVar} is missing or malformed. ` +
      `${svc.label} CTA will be disabled. Set ${svc.envVar} in ` +
      `/app/frontend/.env to enable.`
    );
  }
});

/**
 * @param {'kiwitaxi' | 'localrent'} key
 * @returns {boolean} true when the service has a valid configured URL.
 */
export function isAffiliateServiceEnabled(key) {
  const svc = SERVICES[key];
  return !!(svc && _isValid(svc.url));
}

/**
 * @param {'kiwitaxi' | 'localrent'} key
 * @returns {string} the configured affiliate URL, or '' when disabled.
 */
export function getAffiliateServiceUrl(key) {
  const svc = SERVICES[key];
  return svc && _isValid(svc.url) ? svc.url : '';
}

/**
 * Returns the human-readable partner label (e.g. "Kiwitaxi").
 */
export function getAffiliateServiceLabel(key) {
  const svc = SERVICES[key];
  return svc ? svc.partnerName : '';
}

/**
 * Service metadata for use by analytics/views. Public — no secrets.
 */
export function getAffiliateService(key) {
  const svc = SERVICES[key];
  if (!svc) return null;
  return {
    key: svc.key,
    partnerName: svc.partnerName,
    label: svc.label,
    enabled: _isValid(svc.url),
    envVar: svc.envVar,
  };
}

/**
 * Build the affiliate URL for a service. Today this returns the
 * configured shortlink unchanged — the Travelpayouts affiliate network
 * does not document contextual parameters on these short redirects.
 *
 * If the provider later supports deep-link parameters (destination,
 * pickup IATA, dates…), extend this function to append them safely
 * — never guess undocumented parameter names.
 *
 * @param {'kiwitaxi' | 'localrent'} key
 * @param {object} _context — reserved for future deep-link support.
 * @returns {string}
 */
// eslint-disable-next-line no-unused-vars
export function buildAffiliateUrl(key, _context = {}) {
  return getAffiliateServiceUrl(key);
}

export const AFFILIATE_SERVICES = SERVICES;
